import {
  describeIncompletePhone,
  emailDomainSuggestions,
  formatPhoneAsTyped,
} from '@sitzmark/core';
import { useId } from 'react';

/**
 * The one phone field and the one email field in this product.
 *
 * **Why they are here rather than in each form.** Phone formatting shipped into
 * the customer dialog and not into the register's, because the register has its
 * own copy of that form — so the same shop got smart formatting on one screen
 * and not on the screen it uses two hundred times a day. That is the third time
 * this codebase has paid for the same mistake: `.chip` existed twice with
 * different geometry, `TENDER_LABELS` existed three times with "Cheque" in one
 * and "Check" in another on the same sale, and now this.
 *
 * The rule the repository already had is the right one and was not applied: **a
 * shared primitive belongs in the shared place.** A behaviour worth having on
 * one contact field is worth having on all of them, and the only way that stays
 * true is if there is one field.
 *
 * Both are deliberately thin. They own the *input behaviour* — formatting,
 * completion, the unfinished-value hint — and nothing about layout, so they
 * drop into a quick-add modal, a profile editor, a vendor form and a settings
 * card without any of them knowing about each other.
 */

interface FieldProps {
  readonly label?: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly autoFocus?: boolean;
  /** Enter submits, for the forms that are one press from done. */
  readonly onSubmit?: (() => void) | undefined;
  readonly hint?: string | undefined;
}

/**
 * A phone number that punctuates itself as it is typed (§2.2-5).
 *
 * Ten keystrokes in, `(555) 555-0137` out — so the operator never types a
 * bracket and the shop gets one stored shape rather than the same customer
 * entered three ways, which is what makes digit-to-digit search work.
 */
export function PhoneField({
  label = 'Phone',
  value,
  onChange,
  autoFocus,
  onSubmit,
  hint,
}: FieldProps) {
  const id = useId();
  const problem = describeIncompletePhone(value);

  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className="input"
        autoComplete="off"
        autoFocus={autoFocus}
        inputMode="tel"
        value={value}
        onChange={(event) => onChange(formatPhoneAsTyped(event.target.value))}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && onSubmit !== undefined) {
            event.preventDefault();
            onSubmit();
          }
        }}
      />
      {/*
        Unfinished is not wrong, and an empty field is neither — so this says
        how far off the number is rather than accusing anyone, and says nothing
        at all until they have started (§2.2-7).
      */}
      {problem !== null ? (
        <p className="field__hint field__hint--warning">{problem}</p>
      ) : hint !== undefined ? (
        <p className="field__hint">{hint}</p>
      ) : null}
    </div>
  );
}

/**
 * An email field that offers the domain rather than making them type it.
 *
 * `suggestDomains` is off for addresses that are not a consumer's — a vendor
 * rep and a member of staff are not statistically at gmail, and four wrong
 * suggestions under every field is noise that teaches people to ignore the
 * ones that are right.
 */
export function EmailField({
  label = 'Email',
  value,
  onChange,
  autoFocus,
  onSubmit,
  hint,
  suggestDomains = true,
}: FieldProps & { readonly suggestDomains?: boolean }) {
  const id = useId();
  const suggestions = suggestDomains ? emailDomainSuggestions(value) : [];

  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className="input"
        autoComplete="off"
        autoFocus={autoFocus}
        inputMode="email"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && onSubmit !== undefined) {
            event.preventDefault();
            onSubmit();
          }
        }}
      />
      {hint !== undefined ? <p className="field__hint">{hint}</p> : null}
      {/*
        Finished addresses to tap. Not an inline ghost completion: that guesses
        while somebody is still typing and gets committed by the Enter they were
        going to press anyway (§2.2-3).
      */}
      {suggestions.length > 0 ? (
        <div className="chips quick-add__domains">
          {suggestions.map((suggestion: string) => (
            <button
              key={suggestion}
              type="button"
              className="chip"
              onClick={() => onChange(suggestion)}
            >
              {suggestion}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
